import { Payment } from '../../model/payment';
import { FormControl, FormGroup, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { CommonModule, Location, DatePipe } from '@angular/common';
import { Account } from 'src/app/model/account';
import { catchError, finalize, map, switchMap, takeUntil } from 'rxjs/operators';
import { Observable, Subject, of } from 'rxjs';
import { AlertComponent } from '../alert/alert.component';
import { WindowModule } from '@progress/kendo-angular-dialog';
import { AccountPaymentGridComponent } from '../account-payment-grid/account-payment-grid.component';
import { ChangeDetectorRef } from '@angular/core';
import { AccountService } from 'src/app/core/services/account/account.service';
import { OperatorPaymentService } from '../../core/services/operator-payment/operator-payment.service';
import { DashboardSummaryComponent } from '../dashboard/dashboard-summary/dashboard-summary';
import { PayingOperator } from '../../model/operator';
import { Property } from '../../model/property';
import { SpinnerComponent } from '../spinner/spinner.component';
import { StorageService } from '../../core/services/storage/storage-service.service';
import { PropertyService } from '../../core/services/property/property.service';
import { PaymentDateFilter, createDefaultPaymentDateFilter, isSamePaymentDateFilter } from '../../model/payment_date_filter';

@Component({
  selector: 'app-account-details',
  templateUrl: './account-details.component.html',
  styleUrls: ['./account-details.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AlertComponent,
    WindowModule,
    AccountPaymentGridComponent,
    DashboardSummaryComponent,
    RouterModule,
    SpinnerComponent
  ],
  providers: [DatePipe]
})
export class AccountDetailsComponent implements OnInit, OnDestroy {
  @ViewChild('pageSizeSelect', { static: true }) pageSizeSelect: any;

  public paymentForm: UntypedFormGroup = this.fb.group({
    operator: 0,
    month: this.datePipe.transform(new Date(), 'yyyyMM'),
    searchStr: [''],
    pageSize: 12
  });

  public account: Account;
  public payments: Payment[] = [];
  public accountForm: FormGroup;
  public selectedMonth: string = 'xxx';
  public selectedOperator: number = -1;
  public operatorCount: number;
  public propertyCount: number;
  public wellCount: number;
  public summaryTotal: number;
  public accountProperties: Property[];
  public lastPage = false;
  public loadingMore = false;
  public currentTotal: number;
  public pageLoading: boolean = false;
  public currentPage: number = 1;
  public operators: PayingOperator[] = [];

  /** 0 = all operators. */
  private operatorId: number = 0;
  private pageNo: number = 0;
  private pageSize: number = 12;
  /** Starts equal to the summary's defaults so the first load is already filtered. */
  private dateFilter: PaymentDateFilter = createDefaultPaymentDateFilter();
  /** Payments per page for the current account + operator + date filter. Cleared when any of them change. */
  private pageCache = new Map<number, Payment[]>();
  private totalRecords: number | null = null;
  private pageRequests = new Subject<number>();
  private countRequests = new Subject<void>();
  private unsubscribe = new Subject<void>();
  private accountId: number;

  constructor(
    private fb: UntypedFormBuilder,
    private paymentService: OperatorPaymentService,
    private accountService: AccountService,
    private storageService: StorageService,
    private location: Location,
    private datePipe: DatePipe,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private propertyService: PropertyService
    ) {
  }

  ngOnInit() {
    this.accountForm = new FormGroup({
      month_filter: new FormControl('XXX'),
      operator_filter: new FormControl(-1),
      property_filter: new FormControl(-1)
    });
    this.accountForm.get('month_filter').valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe(value => {
        this.selectedMonth = value;
      }
    );
    this.accountForm.get('operator_filter').valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe(value => {
        this.selectedOperator = value;
        this.cdr.detectChanges();
      }
    );

    this.listenForPageRequests();
    this.listenForCountRequests();

    if (this.storageService.getCurrentAccountId()) {
      this.accountId = this.storageService.getCurrentAccountId();
      this.selectedMonth = 'XXX';
      this.selectedOperator = -1;
      this.getAccount(this.accountId);
      this.reloadPayments();
      this.getCounts();
      this.getOperators();
    } else {
      console.info('No account id found');
      this.router.navigate(['/dashboard']);
    }

  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  // Get methods

  get totalPages(): number {
    if (this.totalRecords === null) {
      return 1;
    }
    return Math.max(1, Math.ceil(this.totalRecords / this.pageSize));
  }

  // Public methods

  public trackByOperatorId(index: number, item: any) {
    return item.id;
  }

  /** Dashboard summary filter changed (Check/Deposit Date toggle, From, To). */
  public onDateFilterChange(filter: PaymentDateFilter) {
    if (isSamePaymentDateFilter(this.dateFilter, filter)) {
      return;
    }
    this.dateFilter = { ...filter };
    this.reloadPayments();
  }

  public onOperatorChange(value: number | string) {
    const operatorId = Number(value) > 0 ? Number(value) : 0;
    if (operatorId === this.operatorId) {
      return;
    }
    this.operatorId = operatorId;
    this.reloadPayments();
  }

  /** Fetches every payment matching the current operator + date filter, for CSV export. */
  public exportFilteredPayments = (): Observable<Payment[]> =>
    this.fetchRemainingRecords().pipe(
      map((remaining) => this.toTotalRecords(remaining)),
      switchMap((total) => total > 0 ? this.fetchPage(0, total) : of([] as Payment[]))
    );

  public getMonths(payments: Payment[]) {
    return payments.reduce((unique, payment) => {
      const transformedDate = this.datePipe.transform(new Date(payment.paymentDate), 'MMM yyyy');
      if (unique.findIndex(date => date === transformedDate) === -1) {
        unique.push(transformedDate);
      }
      return unique;
    }, []);
  }

  private getOperators(): void{
    this.accountService.getAccountPayingOperators(this.accountId).pipe(
      takeUntil(this.unsubscribe)
    ).subscribe((operators: PayingOperator[]) => {
      this.operators = operators;
    });
  }

  private getAccount(ownerId) {
    this.accountService.getAccount(ownerId)
      .pipe(
        takeUntil(this.unsubscribe)
      )
      .subscribe((account: Account) => {
        this.account = account;
      })
  }

  public navigateToPropertyList(){
    this.getAccountProperties();
    this.router.navigate(['/accountProperties']);
  }

  public onBack() {
    this.location.back();
  }

  public onPageSizeChange() {
    this.pageSize = Number(this.pageSizeSelect.nativeElement.value);
    this.reloadPayments();
  }

  public goToFirstPage() {
    this.showPage(0);
  }

  public goToLastPage() {
    this.showPage(this.totalPages - 1);
  }

  public goToNextPage() {
    if (this.currentPage < this.totalPages) {
      this.showPage(this.pageNo + 1);
    }
  }

  public goToPreviousPage() {
    if (this.pageNo > 0) {
      this.showPage(this.pageNo - 1);
    }
  }

  // Private methods

  /** Drops cached pages and the record count, then loads page 1 for the current filters. */
  private reloadPayments() {
    this.pageCache.clear();
    this.totalRecords = null;
    this.countRequests.next();
    this.showPage(0);
  }

  private showPage(pageNo: number) {
    this.pageNo = pageNo;
    this.currentPage = pageNo + 1;
    const cached = this.pageCache.get(pageNo);
    if (cached) {
      this.setPayments(cached);
      return;
    }
    this.pageRequests.next(pageNo);
  }

  private setPayments(payments: Payment[]) {
    this.payments = payments;
    this.currentTotal = this.getCurrentTotal();
  }

  /** switchMap drops responses from superseded requests (e.g. rapid filter changes). */
  private listenForPageRequests() {
    this.pageRequests.pipe(
      switchMap((pageNo) => {
        this.pageLoading = true;
        return this.fetchPage(pageNo, this.pageSize).pipe(
          map((payments) => ({ pageNo, payments })),
          catchError((error) => {
            console.error('Error fetching account payments:', error);
            return of({ pageNo, payments: [] as Payment[] });
          }),
          finalize(() => { this.pageLoading = false; })
        );
      }),
      takeUntil(this.unsubscribe)
    ).subscribe(({ pageNo, payments }) => {
      this.pageCache.set(pageNo, payments);
      if (pageNo === this.pageNo) {
        this.setPayments(payments);
      }
    });
  }

  private listenForCountRequests() {
    this.countRequests.pipe(
      switchMap(() => this.fetchRemainingRecords().pipe(
        catchError((error) => {
          console.error('Error fetching account payment count:', error);
          return of(null as number | null);
        })
      )),
      takeUntil(this.unsubscribe)
    ).subscribe((remaining) => {
      this.totalRecords = remaining === null ? null : this.toTotalRecords(remaining);
    });
  }

  private fetchPage(pageNo: number, pageSize: number): Observable<Payment[]> {
    const { useDepositDate, fromDate, toDate } = this.dateFilter;
    return this.operatorId > 0
      ? this.paymentService.getAccountPaymentsByOperatorByPage(this.accountId, this.operatorId, pageNo, pageSize, useDepositDate, fromDate, toDate)
      : this.paymentService.getAccountPaymentsByPage(this.accountId, pageNo, pageSize, useDepositDate, fromDate, toDate);
  }

  /** The API returns records remaining after page 0, i.e. total - pageSize. */
  private fetchRemainingRecords(): Observable<number> {
    const { useDepositDate, fromDate, toDate } = this.dateFilter;
    const request = this.operatorId > 0
      ? this.paymentService.getAccountPaymentsByOperatorMoreRecords(this.accountId, this.operatorId, 0, this.pageSize, useDepositDate, fromDate, toDate)
      : this.paymentService.getAccountPaymentsMoreRecords(this.accountId, 0, this.pageSize, useDepositDate, fromDate, toDate);
    return request.pipe(map((result) => Number(result?.noRecords ?? 0)));
  }

  private toTotalRecords(remainingAfterFirstPage: number): number {
    return Math.max(0, remainingAfterFirstPage + this.pageSize);
  }

  private getCurrentTotal() {
    return this.payments.reduce((total, payment) => total + payment.checkAmount, 0);
  }

  private getCounts() {
    this.accountService.getAccountSummary(this.accountId).pipe(takeUntil(this.unsubscribe)).subscribe((summary) => {
      this.operatorCount = summary.noOperators;
      this.propertyCount = summary.noProperties;
      this.wellCount = summary.noWells;
    });
  }

  private getAccountProperties() {
    this.propertyService.getAccountProperties(this.accountId).pipe(takeUntil(this.unsubscribe)).subscribe((properties) => {
      this.accountProperties = properties;
      this.storageService.setAccountProperties(properties);
    });
  }

}
